import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../api/client';
import { useSession } from './session';

/** One list, thirty symbols — mirrors the server's cap. */
export const WATCHLIST_MAX = 30;

type WatchlistState = {
  symbols: string[];
  /** True once the server's copy has been merged in for this session. */
  synced: boolean;
  add: (symbol: string) => { ok: boolean; reason?: string };
  remove: (symbol: string) => void;
  toggle: (symbol: string) => { ok: boolean; reason?: string };
  has: (symbol: string) => boolean;
  reorder: (from: number, to: number) => void;
  /** Replace the local list with the server's, then keep pushing local edits. */
  hydrateFrom: (symbols: string[]) => void;
  reset: () => void;
};

/**
 * The local list is the working copy: taps are instant and survive a reload
 * with no network. Every mutation fires a full-list PUT at the server, which
 * is what makes the list follow the account to another browser. Full-state
 * sync is idempotent and makes remove and reorder trivial; at thirty symbols
 * there is nothing to gain from deltas.
 */
export const useWatchlist = create<WatchlistState>()(
  persist(
    (set, get) => {
      /**
       * Fire-and-forget push. A failed sync must never undo a local edit — the
       * local list is the working copy and the next mutation re-pushes the
       * whole thing anyway. Skipped when signed out: there is no account to
       * sync to, and the 401 would raise a spurious session-expired toast.
       */
      const push = (symbols: string[]): void => {
        if (!useSession.getState().token) return;
        void api.putWatchlist(symbols).catch(() => undefined);
      };
      return {
        symbols: [],
        synced: false,

        add: (symbol) => {
          const { symbols } = get();
          if (symbols.includes(symbol)) return { ok: true };
          if (symbols.length >= WATCHLIST_MAX) {
            return { ok: false, reason: `Your watchlist is full (${WATCHLIST_MAX} max)` };
          }
          const next = [...symbols, symbol];
          set({ symbols: next });
          push(next);
          return { ok: true };
        },

        remove: (symbol) => {
          const next = get().symbols.filter((s) => s !== symbol);
          set({ symbols: next });
          push(next);
        },

        toggle: (symbol) => (get().symbols.includes(symbol) ? (get().remove(symbol), { ok: true }) : get().add(symbol)),

        has: (symbol) => get().symbols.includes(symbol),

        reorder: (from, to) => {
          const next = [...get().symbols];
          const [moved] = next.splice(from, 1);
          if (!moved) return;
          next.splice(to, 0, moved);
          set({ symbols: next });
          push(next);
        },

        hydrateFrom: (symbols) => set({ symbols: symbols.slice(0, WATCHLIST_MAX), synced: true }),
        reset: () => set({ symbols: [], synced: false }),
      };
    },
    {
      name: 'dabba-watchlist',
      partialize: (s) => ({ symbols: s.symbols }),
    },
  ),
);
